import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Card,
  CardMedia,
  CardContent,
  Divider,
  Chip,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Avatar,
  useMediaQuery,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

// Icons
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import GrassIcon from "@mui/icons-material/Grass";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import GrainIcon from '@mui/icons-material/Grain';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import BarChartIcon from '@mui/icons-material/BarChart';
import DescriptionIcon from '@mui/icons-material/Description';
import PublicIcon from '@mui/icons-material/Public';
import ScienceIcon from '@mui/icons-material/Science';
import AgricultureIcon from '@mui/icons-material/Agriculture';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.6,
      ease: "easeOut",
    },
  }),
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

// Styled components
const StyledTab = styled(Tab)(({ theme }) => ({
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem',
  minWidth: 120,
  padding: '14px 20px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    color: theme.palette.primary.main,
  },
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
}));

const GradientTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3.5),
  height: '100%',
  borderRadius: 20,
  boxShadow: '0 10px 30px rgba(0,0,0,0.06)',
  transition: 'all 0.4s ease',
  border: '1px solid rgba(0,0,0,0.03)',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
    '& .feature-icon-bg': {
      transform: 'scale(1.1)',
    }
  },
}));

const CircleAvatar = styled(Avatar)(({ theme }) => ({
  width: 65,
  height: 65, 
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.dark,
  marginBottom: theme.spacing(2.5),
  transition: 'all 0.3s ease',
  boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
}));

const NutrientCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  borderRadius: 16,
  boxShadow: '0 8px 25px rgba(0,0,0,0.05)',
  transition: 'all 0.3s ease',
  backgroundColor: 'white',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
  },
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  width: '60px',
  height: '4px',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  borderRadius: '2px',
}));

// Data
const wheatNutritionData = [
  { nutrient: { tr: 'Enerji', en: 'Energy' }, value: '340 kcal', unit: '100g', icon: <RestaurantIcon fontSize="large" /> },
  { nutrient: { tr: 'Karbonhidrat', en: 'Carbohydrate' }, value: '75g', unit: '100g', icon: <GrainIcon fontSize="large" /> },
  { nutrient: { tr: 'Protein', en: 'Protein' }, value: '12-14g', unit: '100g', icon: <ScienceIcon fontSize="large" /> },
  { nutrient: { tr: 'Yağ', en: 'Fat' }, value: '2g', unit: '100g', icon: <WaterDropIcon fontSize="large" /> },
  { nutrient: { tr: 'Lif', en: 'Fiber' }, value: '13g', unit: '100g', icon: <GrassIcon fontSize="large" /> },
  { nutrient: { tr: 'Vas B1', en: 'Vitamin B1' }, value: '0.4mg', unit: '100g', icon: <HealthAndSafetyIcon fontSize="large" /> },
  { nutrient: { tr: 'Vas B3', en: 'Vitamin B3' }, value: '5.7mg', unit: '100g', icon: <HealthAndSafetyIcon fontSize="large" /> },
  { nutrient: { tr: 'Magnezyum', en: 'Magnesium' }, value: '144mg', unit: '100g', icon: <HealthAndSafetyIcon fontSize="large" /> },
];

const wheatQualityStats = [
  {
    name: { tr: "Protein", en: "Protein" },
    value: "12-14%",
    icon: <RestaurantIcon />,
    description: { 
      tr: "Yüksek protein içeriği, kaliteli un ve ekmek üretimi için idealdir.", 
      en: "High protein content is ideal for quality flour and bread production."
    },
  },
  {
    name: { tr: "Nem", en: "Moisture" },
    value: "13%",
    icon: <WaterDropIcon />,
    description: { 
      tr: "Optimum nem seviyesi, ürünün tazeliğini ve kalitesini korur.", 
      en: "Optimum moisture level preserves the freshness and quality of the product."
    },
  },
  {
    name: { tr: "Gluten", en: "Gluten" },
    value: "28%",
    icon: <GrainIcon />,
    description: { 
      tr: "Güçlü gluten yapısı, hamurun elastikiyetini sağlar.", 
      en: "Strong gluten structure provides elasticity to the dough."
    },
  },
  {
    name: { tr: "Saflık", en: "Purity" },
    value: "99.5%",
    icon: <HealthAndSafetyIcon />,
    description: { 
      tr: "Yüksek saflık oranı, premium kalite garantisidir.", 
      en: "High purity ratio guarantees premium quality."
    },
  },
];

const wheatFeatures = [
  {
    title: { tr: "Anadolu Menşei", en: "Anatolian Origin" },
    description: { 
      tr: "Buğday bitkisinin gen merkezi olan Anadolu topraklarında yetiştirilmektedir.", 
      en: "Grown in Anatolian lands, the genetic center of the wheat plant."
    },
    icon: <LocationOnIcon fontSize="large" />,
    color: "#4CAF50",
    bgColor: "#E8F5E9",
  },
  {
    title: { tr: "Çevre Dostu", en: "Eco-Friendly" },
    description: { 
      tr: "Sürdürülebilir tarım teknikleriyle, doğaya saygılı şekilde üretilir.", 
      en: "Produced with sustainable farming techniques, respectful to nature."
    },
    icon: <GrassIcon fontSize="large" />,
    color: "#2196F3",
    bgColor: "#E3F2FD",
  },
  {
    title: { tr: "Kalite Kontrolü", en: "Quality Control" },
    description: { 
      tr: "Her aşamada sıkı kalite kontrolünden geçen premium buğday ürünleri.", 
      en: "Premium wheat products that undergo strict quality control at every stage."
    },
    icon: <CheckCircleOutlineIcon fontSize="large" />,
    color: "#FFC107",
    bgColor: "#FFF8E1",
  },
  {
    title: { tr: "Hızlı Teslimat", en: "Fast Delivery" },
    description: { 
      tr: "Uluslararası standartlarda paketleme ve hızlı sevkiyat imkanı.", 
      en: "International standard packaging and fast shipping options."
    },
    icon: <LocalShippingIcon fontSize="large" />,
    color: "#9C27B0",
    bgColor: "#F3E5F5",
  },
];

// İlgili ürünler listesi
const productItems = [
  {
    id: "barley",
    name: { tr: "Arpa", en: "Barley" },
    image: "/images/barley.jpeg",
  },
  {
    id: "corn",
    name: { tr: "Mısır", en: "Corn" },
    image: "/images/corn.jpeg",
  },
  {
    id: "corn-oil",
    name: { tr: "Mısır Yağı", en: "Corn Oil" },
    image: "/images/corn-oil.jpeg",
  },
  {
    id: "sunflower-oil",
    name: { tr: "Ayçiçek Yağı", en: "Sunflower Oil" },
    image: "/images/sunflower-oil.jpeg",
  },
  {
    id: "chicken",
    name: { tr: "Tavuk", en: "Chicken" },
    image: "/images/chicken.jpeg",
  },
];

const WheatPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [tabValue, setTabValue] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Sayfa başlığını güncelle
  useEffect(() => {
    document.title =
      "SGT Global Trade | " + (i18n.language === "tr" ? "Buğday" : "Wheat");

    // Image preload
    const img = new Image();
    img.src = "/images/wheat.jpeg";
    img.onload = () => setImageLoaded(true);

    // Sayfa yüklendiğinde en üste kaydır
    window.scrollTo(0, 0);
  }, [i18n.language]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FCFCFC",
        minHeight: "100vh",
        pt: { xs: 3, md: 5 },
        pb: 8,
      }}
      id="top"
    >
      {/* Hero Section - Flexbox Approach */}
      <Container maxWidth="xl">
        <Box
          sx={{
            my: 4,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
            alignItems: "center",
          }}
        >
          {/* Left Side - Content */}
          <Box sx={{ flex: "1 1 50%" }}>
            <Chip
              icon={<GrainIcon />}
              label={i18n.language === "tr" ? "Premium Tahıl" : "Premium Grain"}
              color="primary"
              sx={{
                borderRadius: "8px",
                py: 0.5,
                px: 1,
                mb: 2,
              }}
            />

            <GradientTypography
              variant="h2"
              component="h1"
              sx={{
                fontSize: { xs: "1.75rem", md: "2.5rem" },
                mb: 2,
              }}
            >
              {i18n.language === "tr"
                ? "Anadolu'nun Altın Buğdayı"
                : "Anatolian Golden Wheat"}
            </GradientTypography>

            <Typography variant="body1" sx={{ mb: 3 }}>
              {i18n.language === "tr"
                ? "Buğdaygiller familyasına ait olan buğday, insanlık tarihinin en kadim tahıllarından biridir. Anadolu, Batı İran ve Kafkasya'nın gen merkezi olduğu buğday, karasal iklimde yetiştirilir ve temel bir besin kaynağıdır."
                : "Wheat, belonging to the Poaceae family, is one of humanity's most ancient grains. With its genetic center in Anatolia, Western Iran, and the Caucasus, it thrives in continental climates and serves as a fundamental food source."}
            </Typography>

            {/* Stats */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {wheatQualityStats.map((stat, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: "1 1 calc(50% - 8px)",
                    "@media (min-width: 600px)": {
                      flex: "1 1 calc(25% - 12px)",
                    },
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      textAlign: "center",
                      backgroundColor: "white",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: theme.palette.secondary.light,
                        color: theme.palette.primary.main,
                        margin: "0 auto",
                        mb: 1,
                      }}
                    >
                      {stat.icon}
                    </Avatar>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        fontSize: "1.25rem",
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {i18n.language === "tr" ? stat.name.tr : stat.name.en}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Right Side - Image */}
          <Box sx={{ flex: "1 1 50%" }}>
            <Box
              sx={{
                position: "relative",
                height: { xs: "300px", md: "450px" },
                borderRadius: "20px",
                overflow: "hidden",
                boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
              }}
            >
              {!imageLoaded && (
                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    justifyContent: "center",
                    alignItems: "center",
                    bgcolor: "#f5f5f5",
                  }}
                >
                  <CircularProgress color="primary" />
                </Box>
              )}

              {imageLoaded && (
                <img
                  src="/images/wheat.jpeg"
                  alt="Buğday"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              )}

              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                  p: 3,
                  color: "white",
                }}
              >
                <Typography variant="h5" fontWeight={600}>
                  {i18n.language === "tr"
                    ? "Anadolu Buğdayı"
                    : "Anatolian Wheat"}
                </Typography>
                <Typography variant="body1">
                  {i18n.language === "tr"
                    ? "Yüksek kaliteli, verimli ve besleyici"
                    : "High-quality, productive, and nutritious"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Main Content Section with Tabs */}
      <Container maxWidth="xl" sx={{ mt: 8 }}>
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              mb: 5,
              mx: "auto",
              maxWidth: 800,
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons="auto"
              allowScrollButtonsMobile
              centered={!isMobile}
              sx={{
                "& .MuiTabs-indicator": {
                  height: 4,
                  borderRadius: "2px",
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            >
              <StyledTab
                icon={<DescriptionIcon />}
                iconPosition="start"
                label={i18n.language === "tr" ? "Özellikler" : "Features"}
              />
              <StyledTab
                icon={<BarChartIcon />}
                iconPosition="start"
                label={i18n.language === "tr" ? "Besin Değerleri" : "Nutrition"}
              />
            </Tabs>
          </Box>

          {/* Features Tab - REDESIGNED */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ py: 2 }}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                custom={0}
              >
                <Typography
                  variant="h3"
                  component="h2"
                  align="center"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: "1.75rem", md: "2.5rem" },
                  }}
                >
                  {i18n.language === "tr"
                    ? "Buğday Özellikleri"
                    : "Wheat Characteristics"}
                </Typography>

                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <StyledDivider />
                </Box>

                <Typography
                  variant="h6"
                  component="p"
                  align="center"
                  color="text.secondary"
                  sx={{
                    maxWidth: "800px",
                    mx: "auto",
                    mb: 6,
                    fontWeight: 400,
                  }}
                >
                  {i18n.language === "tr"
                    ? "Genellikle kuru ve karasal iklimlerde yetişen buğday, insanoğlunun temel gıda kaynaklarından biridir."
                    : "Typically growing in dry and continental climates, wheat is one of mankind's staple food sources."}
                </Typography>

                <Grid container spacing={4} justifyContent="center">
                  {wheatFeatures.map((feature, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                        custom={index * 0.2}
                        style={{ height: "100%" }}
                      >
                        <FeatureCard sx={{ height: "100%" }}>
                          <Box
                            className="feature-icon-bg"
                            sx={{
                              position: "absolute",
                              top: "-20px",
                              right: "-20px",
                              width: "120px",
                              height: "120px",
                              borderRadius: "50%",
                              backgroundColor: feature.bgColor,
                              zIndex: 0,
                              transition: "all 0.4s ease",
                            }}
                          />

                          <CircleAvatar
                            sx={{
                              backgroundColor: feature.bgColor,
                              color: feature.color,
                              zIndex: 1,
                            }}
                          >
                            {feature.icon}
                          </CircleAvatar>

                          <Typography
                            variant="h6"
                            component="h3"
                            gutterBottom
                            sx={{
                              fontWeight: 700,
                              position: "relative",
                              zIndex: 1,
                              color: feature.color,
                            }}
                          >
                            {i18n.language === "tr"
                              ? feature.title.tr
                              : feature.title.en}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              flexGrow: 1,
                              position: "relative",
                              zIndex: 1,
                              fontSize: "0.95rem",
                              lineHeight: 1.6,
                            }}
                          >
                            {i18n.language === "tr"
                              ? feature.description.tr
                              : feature.description.en}
                          </Typography>
                        </FeatureCard>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ mt: 8 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 3, md: 5 },
                      borderRadius: "24px",
                      backgroundColor: "#F9FAFB",
                      border: "1px solid rgba(0,0,0,0.05)",
                      boxShadow: "0 15px 40px rgba(0,0,0,0.05)",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bottom: 0,
                        width: { xs: "100%", md: "40%" },
                        opacity: { xs: 0.05, md: 0.1 },
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        zIndex: 0,
                      }}
                    />

                    <Box sx={{ position: "relative", zIndex: 1 }}>
                      <Typography
                        variant="h4"
                        component="h3"
                        gutterBottom
                        fontWeight={700}
                        color="primary"
                      >
                        {i18n.language === "tr"
                          ? "Buğday Hakkında"
                          : "About Wheat"}
                      </Typography>

                      <StyledDivider sx={{ mb: 4 }} />

                      <Typography
                        variant="body1"
                        sx={{ mb: 3, lineHeight: 1.8 }}
                      >
                        {i18n.language === "tr"
                          ? "Buğday (Triticum), buğdaygiller familyasından bütün dünyada ıslahı yapılmış tek yıllık otsu bitki cinsidir. Değişik araştırmacıların yaptıkları araştırmaların ışığında buğdayın gen merkezi olarak Anadolu, Batı İran ve Kafkasya kabul edilir. Karasal iklimi tercih eder."
                          : "Wheat (Triticum) is an annual herbaceous plant species from the Poaceae family, cultivated worldwide. According to various researchers, the genetic center of wheat is considered to be Anatolia, Western Iran, and the Caucasus. It prefers continental climates."}
                      </Typography>
                      <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                        {i18n.language === "tr"
                          ? "Buğday; un, yem üretilmesinde kullanılan temel bir besin maddesidir. Kabuğu ayrılabileceği gibi kabuğu ile de öğütülebilir. Buğday aynı zamanda çiftlik hayvanları için bir yem maddesi olarak da yetiştirilmekdedir. Hasattan sonra atık ürün olarak saman balyası çıkar. Enerji miktarı 1.18'dir."
                          : "Wheat is a staple food used in flour and feed production. It can be ground with or without its husk. Wheat is also grown as feed for farm animals. After harvest, straw bales are produced as a waste product. Its energy content is 1.18."}
                      </Typography>

                      <Grid container spacing={3} sx={{ mt: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Box
                            sx={{
                              p: 2,
                              backgroundColor: "white",
                              borderRadius: 3,
                              boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                            }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: "primary.light",
                                color: "primary.dark",
                                mb: 1.5,
                              }}
                            >
                              <AgricultureIcon />
                            </Avatar>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {i18n.language === "tr"
                                ? "Un üretimi"
                                : "Flour production"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {i18n.language === "tr"
                                ? "Ekmek ve unlu mamuller"
                                : "Bread and bakery products"}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Box
                            sx={{
                              p: 2,
                              backgroundColor: "white",
                              borderRadius: 3,
                              boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                            }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: "secondary.light",
                                color: "secondary.dark",
                                mb: 1.5,
                              }}
                            >
                              <RestaurantIcon />
                            </Avatar>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {i18n.language === "tr"
                                ? "Hayvan yemi"
                                : "Animal feed"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {i18n.language === "tr"
                                ? "Çiftlik hayvanları beslenmesi"
                                : "Farm animal nutrition"}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Box
                            sx={{
                              p: 2,
                              backgroundColor: "white",
                              borderRadius: 3,
                              boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                            }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: theme.palette.info.light,
                                color: theme.palette.info.dark,
                                mb: 1.5,
                              }}
                            >
                              <GrainIcon />
                            </Avatar>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {i18n.language === "tr"
                                ? "Makarna üretimi"
                                : "Pasta production"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {i18n.language === "tr"
                                ? "Durum buğdayından üretilir"
                                : "Produced from durum wheat"}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Box
                            sx={{
                              p: 2,
                              backgroundColor: "white",
                              borderRadius: 3,
                              boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                            }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: theme.palette.warning.light,
                                color: theme.palette.warning.dark,
                                mb: 1.5,
                              }}
                            >
                              <LocalShippingIcon />
                            </Avatar>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {i18n.language === "tr"
                                ? "Yan ürün: Saman"
                                : "By-product: Straw"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {i18n.language === "tr"
                                ? "Hayvan altlığı ve endüstriyel kullanım"
                                : "Animal bedding and industrial use"}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Box>
              </motion.div>
            </Box>
          </TabPanel>

          {/* Nutrition Tab - REDESIGNED */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ py: 2 }}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                custom={0}
              >
                <Typography
                  variant="h3"
                  component="h2"
                  align="center"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: "1.75rem", md: "2.5rem" },
                  }}
                >
                  {i18n.language === "tr"
                    ? "Besin Değerleri"
                    : "Nutritional Values"}
                </Typography>

                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <StyledDivider />
                </Box>

                <Typography
                  variant="h6"
                  component="p"
                  align="center"
                  color="text.secondary"
                  sx={{
                    maxWidth: "800px",
                    mx: "auto",
                    mb: 6,
                    fontWeight: 400,
                  }}
                >
                  {i18n.language === "tr"
                    ? "Buğday, yüksek besin değeri ile sağlıklı bir beslenmenin temel taşlarından biridir."
                    : "Wheat, with its high nutritional value, is one of the cornerstones of a healthy diet."}
                </Typography>

                {/* Nutrient Cards in Grid - Modern Design */}

                <Grid container spacing={4}>
                  {/* Table - Left Column */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 2, md: 4 },
                        borderRadius: "20px",
                        backgroundColor: "white",
                        boxShadow: "0 15px 35px rgba(0,0,0,0.05)",
                        height: "100%",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "8px",
                          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        }}
                      />

                      <Typography
                        variant="h5"
                        component="h3"
                        gutterBottom
                        fontWeight={700}
                        color="primary"
                        sx={{ mt: 1 }}
                      >
                        {i18n.language === "tr"
                          ? "Besin Değerleri Tablosu"
                          : "Nutritional Values Table"}
                      </Typography>

                      <TableContainer sx={{ mt: 3 }}>
                        <Table aria-label="besin değerleri tablosu">
                          <TableHead>
                            <TableRow
                              sx={{
                                backgroundColor: theme.palette.primary.light,
                                "& th": {
                                  fontWeight: 700,
                                  color: theme.palette.primary.dark,
                                  fontSize: "0.95rem",
                                },
                              }}
                            >
                              <TableCell>
                                {i18n.language === "tr" ? "Besin" : "Nutrient"}
                              </TableCell>
                              <TableCell align="right">
                                {i18n.language === "tr" ? "Değer" : "Value"}
                              </TableCell>
                              <TableCell align="right">
                                {i18n.language === "tr" ? "Birim" : "Unit"}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {wheatNutritionData.map((row, index) => (
                              <TableRow
                                key={index}
                                sx={{
                                  "&:nth-of-type(even)": {
                                    backgroundColor: "#f8f9fa",
                                  },
                                  transition: "all 0.2s",
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.primary.lighter ||
                                      "#e3f2fd",
                                  },
                                }}
                              >
                                <TableCell
                                  component="th"
                                  scope="row"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {i18n.language === "tr"
                                    ? row.nutrient.tr
                                    : row.nutrient.en}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    fontWeight: 600,
                                    color: theme.palette.primary.main,
                                  }}
                                >
                                  {row.value}
                                </TableCell>
                                <TableCell align="right">{row.unit}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Grid>
                  {/* Health Benefits - Right Column */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 2, md: 4 },
                        borderRadius: "20px",
                        backgroundColor: "white",
                        boxShadow: "0 15px 35px rgba(0,0,0,0.05)",
                        height: "100%",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "8px",
                          background: `linear-gradient(90deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                        }}
                      />

                      <Typography
                        variant="h5"
                        component="h3"
                        gutterBottom
                        fontWeight={700}
                        color="primary"
                        sx={{ mt: 1 }}
                      >
                        {i18n.language === "tr"
                          ? "Sağlık Faydaları"
                          : "Health Benefits"}
                      </Typography>

                      <List>
                        <ListItem
                          sx={{
                            py: 2,
                            px: 3,
                            mb: 2,
                            borderRadius: 2,
                            backgroundColor:
                              theme.palette.primary.lighter || "#e3f2fd",
                            transition: "all 0.2s",
                            "&:hover": {
                              transform: "translateX(5px)",
                              boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                            },
                          }}
                        >
                          <ListItemIcon>
                            <Avatar
                              sx={{
                                bgcolor: theme.palette.primary.light,
                                color: theme.palette.primary.dark,
                              }}
                            >
                              <HealthAndSafetyIcon />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" fontWeight={700}>
                                {i18n.language === "tr"
                                  ? "Lifli İçerik"
                                  : "Fiber Content"}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {i18n.language === "tr"
                                  ? "Sindirim sistemi sağlığına katkı sağlar"
                                  : "Contributes to digestive system health"}
                              </Typography>
                            }
                          />
                        </ListItem>

                        <ListItem
                          sx={{
                            py: 2,
                            px: 3,
                            mb: 2,
                            borderRadius: 2,
                            backgroundColor:
                              theme.palette.secondary.lighter || "#e8f5e9",
                            transition: "all 0.2s",
                            "&:hover": {
                              transform: "translateX(5px)",
                              boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                            },
                          }}
                        >
                          <ListItemIcon>
                            <Avatar
                              sx={{
                                bgcolor: theme.palette.secondary.light,
                                color: theme.palette.secondary.dark,
                              }}
                            >
                              <ScienceIcon />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" fontWeight={700}>
                                {i18n.language === "tr"
                                  ? "B Vitaminleri"
                                  : "B Vitamins"}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {i18n.language === "tr"
                                  ? "Enerji metabolizması için önemlidir"
                                  : "Important for energy metabolism"}
                              </Typography>
                            }
                          />
                        </ListItem>

                        <ListItem
                          sx={{
                            py: 2,
                            px: 3,
                            mb: 2,
                            borderRadius: 2,
                            backgroundColor:
                              theme.palette.info.lighter || "#e3f2fd",
                            transition: "all 0.2s",
                            "&:hover": {
                              transform: "translateX(5px)",
                              boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                            },
                          }}
                        >
                          <ListItemIcon>
                            <Avatar
                              sx={{
                                bgcolor: theme.palette.info.light,
                                color: theme.palette.info.dark,
                              }}
                            >
                              <PublicIcon />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" fontWeight={700}>
                                {i18n.language === "tr"
                                  ? "Mineraller"
                                  : "Minerals"}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {i18n.language === "tr"
                                  ? "Magnezyum, çinko ve demir açısından zengin"
                                  : "Rich in magnesium, zinc, and iron"}
                              </Typography>
                            }
                          />
                        </ListItem>

                        <ListItem
                          sx={{
                            py: 2,
                            px: 3,
                            borderRadius: 2,
                            backgroundColor:
                              theme.palette.warning.lighter || "#fff8e1",
                            transition: "all 0.2s",
                            "&:hover": {
                              transform: "translateX(5px)",
                              boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                            },
                          }}
                        >
                          <ListItemIcon>
                            <Avatar
                              sx={{
                                bgcolor: theme.palette.warning.light,
                                color: theme.palette.warning.dark,
                              }}
                            >
                              <RestaurantIcon />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" fontWeight={700}>
                                {i18n.language === "tr"
                                  ? "Protein Kaynağı"
                                  : "Protein Source"}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {i18n.language === "tr"
                                  ? "Vücut dokularının onarımı ve büyümesi için gerekli"
                                  : "Essential for body tissue repair and growth"}
                              </Typography>
                            }
                          />
                        </ListItem>
                      </List>
                    </Paper>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 4 }}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: "16px",
                      overflow: "hidden",
                      backgroundColor: "white",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
                    }}
                  >
                    <Grid container alignItems="center">
                      <Grid item xs={12} md={7}>
                        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            color="primary"
                            sx={{ mb: 0.5 }}
                          >
                            {i18n.language === "tr"
                              ? "Sağlıklı Yaşam İçin Buğday"
                              : "Wheat for Healthy Living"}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "0.85rem", lineHeight: 1.6 }}
                          >
                            {i18n.language === "tr"
                              ? "Tam buğday ürünleri, kompleks karbonhidratlar ve mineral açısından zengindir. Rafine una göre daha fazla besin değeri taşır ve kan şekerini daha yavaş yükseltir."
                              : "Whole wheat products are rich in complex carbohydrates and minerals. They carry more nutritional value than refined flour and raise blood sugar more slowly."}
                          </Typography>
                        </CardContent>
                      </Grid>
                    </Grid>
                  </Card>
                </Box>
              </motion.div>
            </Box>
          </TabPanel>
        </Box>
      </Container>
      {/* İlgili Ürünler Bölümü - Mobil Görünüm İçin Düzeltilmiş Versiyon */}
      <Container maxWidth="xl" sx={{ mt: 8 }}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeInUp}
          custom={0}
        >
          <Typography
            variant="h3"
            component="h2"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 1,
              fontSize: { xs: "1.75rem", md: "2.5rem" },
            }}
          >
            {i18n.language === "tr" ? "İlgili Ürünler" : "Related Products"}
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <StyledDivider />
          </Box>

          <Typography
            variant="h6"
            component="p"
            align="center"
            color="text.secondary"
            sx={{
              maxWidth: "800px",
              mx: "auto",
              mb: 6,
              fontWeight: 400,
            }}
          >
            {i18n.language === "tr"
              ? "Diğer tahıl ve tarım ürünlerimizi inceleyin"
              : "Explore our other grain and agricultural products"}
          </Typography>

          {/* Düzeltilmiş kaydırma alanı ve grid yapısı */}
          <Box
            sx={{
              overflowX: "auto",
              pb: 2,
              "&::-webkit-scrollbar": {
                height: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0,0,0,0.1)",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "rgba(0,0,0,0.02)",
              },
              WebkitOverflowScrolling: "touch", // Daha akıcı kaydırma için
              scrollbarWidth: "thin", // Firefox için
            }}
          >
            <Box
              sx={{
                display: "flex",
                width: { xs: "max-content", lg: "100%" },
                gap: 3,
                px: { xs: 1, md: 0 },
              }}
            >
              {productItems.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    width: { xs: "240px", sm: "280px", md: "300px", lg: "1fr" },
                    flex: { lg: "1 1 0" },
                  }}
                >
                  <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeInUp}
                    custom={index * 0.2}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: "16px",
                        overflow: "hidden",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                        transition: "all 0.3s ease",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        "&:hover": {
                          transform: "translateY(-10px)",
                          boxShadow: "0 15px 35px rgba(0,0,0,0.1)",
                        },
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="180"
                        image={item.image}
                        alt={item.name[i18n.language === "tr" ? "tr" : "en"]}
                        sx={{
                          transition: "transform 0.5s ease",
                          "&:hover": {
                            transform: "scale(1.05)",
                          },
                        }}
                      />
                      <CardContent
                        sx={{
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          variant="h6"
                          component="h3"
                          gutterBottom
                          fontWeight={600}
                        >
                          {item.name[i18n.language === "tr" ? "tr" : "en"]}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: "auto" }}
                        >
                          {i18n.language === "tr"
                            ? "Yüksek kaliteli tarım ürünlerimiz"
                            : "Our high-quality agricultural products"}
                        </Typography>
                        <Button
                          variant="outlined"
                          color="primary"
                          endIcon={<ArrowForwardIcon />}
                          onClick={() => navigate(`/products/${item.id}`)}
                          sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 600,
                            mt: 2,
                            alignSelf: "flex-start",
                          }}
                        >
                          {i18n.language === "tr" ? "Detaylar" : "Details"}
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Box>
              ))}
            </Box>
          </Box>
        </motion.div>
      </Container>

      {/* Hemen Teklif Alın - Modernize edilmiş ve sayfanın en altına taşınmış */}
      <Box sx={{ my: 8, py: 4 }}>
        <Container maxWidth="xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            custom={0}
          >
            <Box
              sx={{
                borderRadius: "24px",
                overflow: "hidden",
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
                position: "relative",
              }}
            >
              {/* Background Pattern */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  opacity: 0.1,
                  zIndex: 0,
                }}
              />

              <Grid
                container
                alignItems="center"
                sx={{ position: "relative", zIndex: 1 }}
              >
                <Grid item xs={12} md={7} sx={{ p: { xs: 4, md: 5 } }}>
                  <Typography
                    variant="h2"
                    component="h2"
                    sx={{
                      color: "white",
                      fontWeight: 800,
                      fontSize: { xs: "2rem", md: "2.5rem" },
                      textShadow: "0 2px 10px rgba(0,0,0,0.2)",
                      mb: 2,
                    }}
                  >
                    {i18n.language === "tr"
                      ? "Premium Buğday Tedariği"
                      : "Premium Wheat Supply"}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: "white",
                      opacity: 0.9,
                      mt: 1,
                      mb: 3,
                      fontWeight: 400,
                      textShadow: "0 1px 5px rgba(0,0,0,0.1)",
                    }}
                  >
                    {i18n.language === "tr"
                      ? "Rekabetçi fiyatlar ve güvenilir tedarik zinciri"
                      : "Competitive prices and reliable supply chain"}
                  </Typography>

                  <Box
                    sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 3 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CheckCircleOutlineIcon
                        sx={{
                          color: "white",
                          mr: 1,
                          fontSize: 28,
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          color: "white",
                          fontWeight: 500,
                        }}
                      >
                        {i18n.language === "tr"
                          ? "Yüksek Kalite"
                          : "High Quality"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CheckCircleOutlineIcon
                        sx={{
                          color: "white",
                          mr: 1,
                          fontSize: 28,
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          color: "white",
                          fontWeight: 500,
                        }}
                      >
                        {i18n.language === "tr"
                          ? "Esnek Sevkiyat"
                          : "Flexible Shipping"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CheckCircleOutlineIcon
                        sx={{
                          color: "white",
                          mr: 1,
                          fontSize: 28,
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          color: "white",
                          fontWeight: 500,
                        }}
                      >
                        {i18n.language === "tr"
                          ? "Özel Çözümler"
                          : "Custom Solutions"}
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate("/quote")}
                    sx={{
                      borderRadius: "12px",
                      py: 1.8,
                      px: 5,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "1.1rem",
                      backgroundColor: "white",
                      color: theme.palette.primary.main,
                      boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
                      "&:hover": {
                        backgroundColor: "white",
                        transform: "translateY(-3px)",
                        boxShadow: "0 12px 25px rgba(0,0,0,0.25)",
                      },
                    }}
                  >
                    {i18n.language === "tr"
                      ? "Hemen Teklif Al"
                      : "Get a Quote Now"}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};
// Tab Panel Component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export default WheatPage;